import { create } from 'zustand'
import type { Script } from '@/types/script'
import { scriptsApi } from '@/services/api/scripts'

interface ScriptStore {
  scripts: Script[]
  currentScript: Script | null
  isLoading: boolean
  error: string | null
  loadScripts: () => Promise<void>
  loadScript: (id: string) => Promise<void>
  createScript: (name: string, dslCode: string, selectedDatasetId?: string) => Promise<{ script: Script; compilation?: any }>
  updateScript: (id: string, name?: string, dslCode?: string, selectedDatasetId?: string | null) => Promise<{ script: Script; compilation?: any }>
  saveScript: (id: string | null, name: string, dslCode: string) => Promise<Script>
  deleteScript: (id: string) => Promise<void>
  setCurrentScript: (script: Script | null) => void
}

export const useScriptStore = create<ScriptStore>((set) => ({
  scripts: [],
  currentScript: null,
  isLoading: false,
  error: null,

  loadScripts: async () => {
    set({ isLoading: true, error: null })
    try {
      const result = await scriptsApi.list()
      // Extract scripts array from paginated response
      const scriptsArray = result.scripts || []
      set({ scripts: scriptsArray, isLoading: false })
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Failed to load scripts',
        isLoading: false,
        scripts: [],
      })
    }
  },

  loadScript: async (id: string) => {
    set({ isLoading: true, error: null })
    try {
      const script = await scriptsApi.get(id)
      set({ currentScript: script, isLoading: false })
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Failed to load script',
        isLoading: false,
      })
    }
  },

  createScript: async (name: string, dslCode: string, selectedDatasetId?: string) => {
    set({ isLoading: true, error: null })
    try {
      const result = await scriptsApi.create({ name, dslCode, selectedDatasetId })
      set((state) => ({
        scripts: [...state.scripts, result.script],
        currentScript: result.script, // Set as current script after creation
        isLoading: false,
      }))
      return result
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Failed to create script',
        isLoading: false,
      })
      throw error
    }
  },

  updateScript: async (id: string, name?: string, dslCode?: string, selectedDatasetId?: string | null) => {
    set({ isLoading: true, error: null })
    try {
      const result = await scriptsApi.update(id, { name, dslCode, selectedDatasetId })
      set((state) => ({
        scripts: state.scripts.map((s) => (s.id === id ? result.script : s)),
        currentScript: state.currentScript?.id === id ? result.script : state.currentScript,
        isLoading: false,
      }))
      return result
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Failed to update script',
        isLoading: false,
      })
      throw error
    }
  },

  saveScript: async (id: string | null, name: string, dslCode: string) => {
    set({ isLoading: true, error: null })
    try {
      let script: Script
      if (!id || id === 'new') {
        // Create new script
        const result = await scriptsApi.create({ name, dslCode })
        script = result.script
        set((state) => ({
          scripts: [...state.scripts, script],
          currentScript: script,
          isLoading: false,
        }))
      } else {
        // Update existing script
        const result = await scriptsApi.update(id, { name, dslCode })
        script = result.script
        set((state) => ({
          scripts: state.scripts.map((s) => (s.id === id ? script : s)),
          currentScript: state.currentScript?.id === id ? script : state.currentScript,
          isLoading: false,
        }))
      }
      return script
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Failed to save script',
        isLoading: false,
      })
      throw error
    }
  },

  deleteScript: async (id: string) => {
    set({ isLoading: true, error: null })
    try {
      await scriptsApi.delete(id)
      set((state) => ({
        scripts: state.scripts.filter((s) => s.id !== id),
        currentScript: state.currentScript?.id === id ? null : state.currentScript,
        isLoading: false,
      }))
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Failed to delete script',
        isLoading: false,
      })
      throw error
    }
  },

  setCurrentScript: (script: Script | null) => {
    set({ currentScript: script })
  },
}))
